import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import api from '../lib/api';

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post('/api/auth/forgot-password', { email });
      setSubmitted(true);
      toast.success('Reset-Link wurde gesendet!');
    } catch (error) {
      // Still show success to not reveal if email exists
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 hex-pattern">
      <Link to="/login" className="absolute top-6 left-6 flex items-center gap-2 text-tunis-blue hover:text-tunis-teal transition-colors">
        <ArrowLeft className="w-5 h-5" />
        Zurück zum Login
      </Link>
      
      <div className="w-full max-w-md space-y-6 animate-fade-in">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <img src={LOGO_URL} alt="Tunis Kick" className="h-20 w-20 mx-auto rounded-full shadow-lg" />
          </Link>
          <h1 className="mt-4 font-display font-bold text-2xl text-tunis-blue">Passwort vergessen?</h1>
          <p className="text-muted-foreground">Kein Problem, wir helfen dir!</p>
        </div>

        <Card className="border-slate-200 shadow-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-tunis-teal" />
              Passwort zurücksetzen
            </CardTitle>
            <CardDescription>
              Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen deines Passworts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail-Adresse</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="deine@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="input-forgot-email"
                    className="h-12"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary h-12"
                  disabled={loading}
                  data-testid="btn-send-reset"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Reset-Link senden'
                  )}
                </Button>
              </form>
            ) : (
              <div className="text-center space-y-4 py-4">
                <div className="w-16 h-16 mx-auto bg-tunis-teal/10 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-tunis-teal" />
                </div>
                <div>
                  <h3 className="font-semibold text-tunis-blue">E-Mail gesendet!</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Wenn ein Konto mit dieser E-Mail existiert, erhältst du in Kürze einen Link zum Zurücksetzen deines Passworts.
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Keine E-Mail erhalten?{' '}
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-tunis-teal hover:underline font-medium"
                  >
                    Erneut versuchen
                  </button>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          Erinnerst du dich an dein Passwort?{' '}
          <Link to="/login" className="font-semibold text-tunis-teal hover:underline">
            Jetzt anmelden
          </Link>
        </p>
      </div>
    </div>
  );
}
